import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  alpha,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  Badge,
  Fade,
  Zoom,
  LinearProgress,
  Alert,
  Snackbar,
  DialogContentText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Import icons
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import GroupIcon from '@mui/icons-material/Group';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HistoryIcon from '@mui/icons-material/History';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Import Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  Filler,
} from 'chart.js';
import { Bar, Doughnut, Pie } from 'react-chartjs-2';

// Import local components
import Sidebar from '../../../../components/sidebars/Sidebar_Gm';
import TopNav from '../../../../components/TopNav_hotelStaff';
import axios from 'axios';
import * as XLSX from 'xlsx';

// Register ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  Filler
);

// Role color mapping
const ROLE_COLORS = {
  'General_Manager': '#6366F1',
  'hod': '#10B981',
  'receptionist': '#3B82F6',
  'Restaurant/Bar': '#F59E0B',
  'Storekeeping': '#8B5CF6',
  'Account': '#EC4899',
  'Cashier': '#06B6D4',
  'Requisition': '#F97316',
};

const DEPARTMENT_COLORS = {
  'General Manager': '#6366F1',
  'Reception': '#3B82F6',
  'Bar/Restaurant': '#F59E0B',
  'Store Keeping': '#8B5CF6',
  'Account & Auditing': '#EC4899',
  'Cashier': '#06B6D4',
  'Housekeeping': '#10B981',
  'HR': '#F97316',
  'Maintenance': '#EF4444',
  'Security': '#6B7280',
};

const Gm_StaffDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [staffDetailOpen, setStaffDetailOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  
  // Department dialog states
  const [departmentDialogOpen, setDepartmentDialogOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departmentStaff, setDepartmentStaff] = useState([]);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('full_name');
  const [order, setOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');

  const userProfile = JSON.parse(localStorage.getItem("userProfile"));
  const hotel_code = userProfile?.hotel_code || "";
  const hotelName = userProfile?.hotel_name || "Hotel";

  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/login');
  };

  const handleNotificationClick = () => {
    // Handle notifications
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    if (!hotel_code) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const baseURL = process.env.REACT_APP_API_BASE_URL || '';
      
      const response = await axios.get(`${baseURL}/hotel/staff/gm_staff_dashboard_data.php`, {
        params: { hotel_code }
      });
      
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showSnackbar('Failed to fetch dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [hotel_code]);

  // Handle department card click
  const handleDepartmentClick = (department) => {
    const staffList = dashboardData?.staff_list || [];
    const filteredStaff = staffList.filter(s => s.department === department);
    setSelectedDepartment(department);
    setDepartmentStaff(filteredStaff);
    setDepartmentDialogOpen(true);
  };

  // Handle delete staff
  const handleDeleteClick = (staff) => {
    setStaffToDelete(staff);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!staffToDelete) return;
    
    setDeleting(true);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.delete(`${baseURL}/hotel/staff/delete_staffGM.php`, {
        data: { 
          id: staffToDelete.id,
          hotel_code: hotel_code 
        }
      });
      
      if (response.data.success) {
        showSnackbar(`Staff member ${staffToDelete.full_name} deleted successfully`, 'success');
        setDeleteDialogOpen(false);
        setStaffToDelete(null);
        // Refresh data
        fetchDashboardData();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error deleting staff:', error);
      showSnackbar(error.response?.data?.message || 'Failed to delete staff member', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Filter and sort staff list
  const filteredStaff = useMemo(() => {
    if (!dashboardData?.staff_list) return [];
    
    let filtered = [...dashboardData.staff_list];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(s => 
        s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.staff_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply department filter
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(s => s.department === departmentFilter);
    }
    
    // Apply role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(s => s.role === roleFilter);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      
      if (typeof aVal === 'string') {
        aVal = aVal?.toLowerCase() || '';
        bVal = bVal?.toLowerCase() || '';
      }
      
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [dashboardData, searchTerm, departmentFilter, roleFilter, orderBy, order]);

  // Paginated staff
  const paginatedStaff = filteredStaff.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleStaffClick = (staff) => {
    setSelectedStaff(staff);
    setStaffDetailOpen(true);
  };

  // Export to Excel
  const exportToExcel = () => {
    const exportData = filteredStaff.map(s => ({
      'Staff ID': s.staff_id,
      'Full Name': s.full_name,
      'Department': s.department,
      'Role': s.role,
      'Email': s.email,
      'Phone': s.phone,
      'Gender': s.gender,
      'Hire Date': s.hire_date,
      'Last Updated': s.last_updated
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Staff_List');
    XLSX.writeFile(wb, `staff_list_${new Date().toISOString().split('T')[0]}.xlsx`);
    handleMenuClose();
    showSnackbar('Export successful!', 'success');
  };

  const summary = dashboardData?.summary || {};
  const departments = dashboardData?.departments || [];
  const staffByRole = dashboardData?.staff_by_role || [];
  const recentActivity = dashboardData?.recent_activity || [];
  const departmentStats = dashboardData?.department_stats || [];
  const staffPerformance = dashboardData?.staff_performance || [];
  const recentLogins = dashboardData?.recent_logins || [];

  // Chart data for role distribution
  const roleChartData = {
    labels: [...new Set(staffByRole.map(r => r.role))],
    datasets: [{
      data: [...new Set(staffByRole.map(r => 
        staffByRole.filter(sr => sr.role === r.role).reduce((sum, sr) => sum + sr.count, 0)
      ))],
      backgroundColor: [...new Set(staffByRole.map(r => ROLE_COLORS[r.role] || '#6B7280'))],
      borderColor: '#fff',
      borderWidth: 2,
    }],
  };

  // Chart data for department distribution
  const departmentChartData = {
    labels: departments.map(d => d.department),
    datasets: [{
      data: departments.map(d => d.staff_count),
      backgroundColor: departments.map(d => DEPARTMENT_COLORS[d.department] || '#6B7280'),
      borderColor: '#fff',
      borderWidth: 2,
    }],
  };

  if (!hotel_code) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Please log in again</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box sx={{
        flexGrow: 1,
        p: { xs: 2, md: 3 },
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
        mt: 0,
        maxWidth: '1400px',
        mx: 'auto',
      }}>
        <TopNav onLogout={handleLogout} onNotificationClick={handleNotificationClick} toggleSidebar={toggleSidebar} />
        
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Staff Management Dashboard</Typography>
            <Typography variant="body2" color="text.secondary">{hotelName} • Workforce Analytics</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchDashboardData} sx={{ bgcolor: 'white' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export">
              <IconButton onClick={handleMenuOpen} sx={{ bgcolor: 'white' }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={exportToExcel}>Export Staff List to Excel</MenuItem>
            </Menu>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <>
            {/* Staff Summary Cards - Clickable Departments */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              📊 Department Overview (Click any card to view staff)
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {departments.map((dept, idx) => (
                <Grid item xs={12} sm={6} md={4} lg={2.4} key={idx}>
                  <Zoom in={true} style={{ transitionDelay: `${idx * 50}ms` }}>
                    <Card 
                      sx={{ 
                        borderRadius: 3, 
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { 
                          transform: 'translateY(-4px)', 
                          boxShadow: 6,
                          borderTop: `4px solid ${DEPARTMENT_COLORS[dept.department] || '#6366F1'}`
                        },
                        borderTop: `2px solid ${alpha(DEPARTMENT_COLORS[dept.department] || '#6366F1', 0.5)}`
                      }}
                      onClick={() => handleDepartmentClick(dept.department)}
                    >
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body2" color="text.secondary">{dept.department}</Typography>
                            <Typography variant="h4" fontWeight={700}>{dept.staff_count}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Staff Members
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: alpha(DEPARTMENT_COLORS[dept.department] || '#6366F1', 0.1), color: DEPARTMENT_COLORS[dept.department] || '#6366F1' }}>
                            <BusinessIcon />
                          </Avatar>
                        </Box>
                        {dept.hod_count > 0 && (
                          <Chip 
                            label="HOD Assigned" 
                            size="small" 
                            color="success" 
                            icon={<AdminPanelSettingsIcon />}
                            sx={{ mt: 1, height: 24, fontSize: '0.7rem' }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Zoom>
                </Grid>
              ))}
            </Grid>

            {/* Summary Stats Row */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={6} sm={3} md={2.4}>
                <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center', bgcolor: '#6366F1', color: 'white' }}>
                  <Typography variant="h4" fontWeight={700}>{summary.total_staff || 0}</Typography>
                  <Typography variant="caption">Total Staff</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3} md={2.4}>
                <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center', bgcolor: '#10B981', color: 'white' }}>
                  <Typography variant="h4" fontWeight={700}>{summary.hod_count || 0}</Typography>
                  <Typography variant="caption">HOD</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3} md={2.4}>
                <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center', bgcolor: '#F59E0B', color: 'white' }}>
                  <Typography variant="h4" fontWeight={700}>{summary.fb_staff_count || 0}</Typography>
                  <Typography variant="caption">F&B Staff</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3} md={2.4}>
                <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center', bgcolor: '#8B5CF6', color: 'white' }}>
                  <Typography variant="h4" fontWeight={700}>{(summary.storekeeper_count || 0) + (summary.accountant_count || 0)}</Typography>
                  <Typography variant="caption">Store & Accounts</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3} md={2.4}>
                <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center', bgcolor: '#06B6D4', color: 'white' }}>
                  <Typography variant="h4" fontWeight={700}>{summary.cashier_count || 0}</Typography>
                  <Typography variant="caption">Cashiers</Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Tabs Section */}
            <Paper sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
              <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ px: 2, pt: 1, borderBottom: '1px solid #e2e8f0' }}>
                <Tab icon={<AssessmentIcon />} label="Overview" />
                <Tab icon={<PeopleIcon />} label="Staff Directory" />
                <Tab icon={<HistoryIcon />} label="Recent Activity" />
                <Tab icon={<TrendingUpIcon />} label="Performance" />
              </Tabs>

              {/* Overview Tab */}
              {activeTab === 0 && (
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>Staff by Role</Typography>
                      <Box sx={{ height: 300 }}>
                        <Pie 
                          data={roleChartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: { legend: { position: 'bottom', labels: { font: { size: 11 } } } }
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>Staff by Department</Typography>
                      <Box sx={{ height: 300 }}>
                        <Pie 
                          data={departmentChartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: { legend: { position: 'bottom', labels: { font: { size: 11 } } } }
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Department Breakdown */}
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 3, mb: 2 }}>Department Breakdown</Typography>
                  <Grid container spacing={2}>
                    {departments.map((dept, idx) => (
                      <Grid item xs={12} sm={6} md={4} key={idx}>
                        <Card sx={{ borderRadius: 2, cursor: 'pointer', '&:hover': { boxShadow: 3 } }} onClick={() => handleDepartmentClick(dept.department)}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle1" fontWeight={600}>{dept.department}</Typography>
                              <Chip label={`${dept.staff_count} staff`} size="small" color="primary" />
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="caption" color="text.secondary" display="block">
                              HOD: {dept.hod_count > 0 ? 'Assigned' : 'Not Assigned'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Roles: {dept.roles?.split(',').slice(0, 3).join(', ')}{dept.roles?.split(',').length > 3 ? '...' : ''}
                            </Typography>
                            <Box sx={{ mt: 1.5 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={Math.min((dept.staff_count / 20) * 100, 100)} 
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Staff Directory Tab - With Delete Button */}
              {activeTab === 1 && (
                <Box sx={{ p: 3 }}>
                  {/* Filters */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <TextField
                      placeholder="Search by name, ID, email, department..."
                      size="small"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{ minWidth: 250 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Department</InputLabel>
                        <Select
                          value={departmentFilter}
                          onChange={(e) => setDepartmentFilter(e.target.value)}
                          label="Department"
                        >
                          <MenuItem value="all">All Departments</MenuItem>
                          {departments.map((dept, idx) => (
                            <MenuItem key={idx} value={dept.department}>{dept.department}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Role</InputLabel>
                        <Select
                          value={roleFilter}
                          onChange={(e) => setRoleFilter(e.target.value)}
                          label="Role"
                        >
                          <MenuItem value="all">All Roles</MenuItem>
                          {[...new Set(staffByRole.map(r => r.role))].map((role, idx) => (
                            <MenuItem key={idx} value={role}>{role}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {/* Staff Table */}
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell>Staff ID</TableCell>
                          <TableCell>Full Name</TableCell>
                          <TableCell>Department</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Contact</TableCell>
                          <TableCell>Hire Date</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedStaff.map((staff) => (
                          <TableRow key={staff.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                                {staff.staff_id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(ROLE_COLORS[staff.role] || '#6B7280', 0.1) }}>
                                  {staff.full_name?.charAt(0)}
                                </Avatar>
                                <Typography variant="body2" fontWeight={500}>{staff.full_name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={staff.department} 
                                size="small"
                                sx={{ bgcolor: alpha(DEPARTMENT_COLORS[staff.department] || '#6B7280', 0.1), color: DEPARTMENT_COLORS[staff.department] || '#6B7280' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={staff.role} 
                                size="small"
                                sx={{ bgcolor: alpha(ROLE_COLORS[staff.role] || '#6B7280', 0.1), color: ROLE_COLORS[staff.role] || '#6B7280' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="caption" display="block">{staff.email}</Typography>
                                <Typography variant="caption" color="text.secondary">{staff.phone}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>{staff.hire_date}</TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={0.5} justifyContent="center">
                                <Tooltip title="View Details">
                                  <IconButton size="small" onClick={() => handleStaffClick(staff)}>
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Staff">
                                  <IconButton 
                                    size="small" 
                                    color="error" 
                                    onClick={() => handleDeleteClick(staff)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={filteredStaff.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                  />
                </Box>
              )}

              {/* Recent Activity Tab */}
              {activeTab === 2 && (
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>Recently Joined Staff (Last 30 Days)</Typography>
                      {recentActivity.length === 0 ? (
                        <Paper sx={{ p: 3, textAlign: 'center' }}>
                          <Typography color="text.secondary">No recent staff additions</Typography>
                        </Paper>
                      ) : (
                        <Stack spacing={1}>
                          {recentActivity.map((staff, idx) => (
                            <Paper key={idx} sx={{ p: 2, borderRadius: 2, '&:hover': { bgcolor: '#f8fafc' } }}>
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                  <Typography variant="subtitle2" fontWeight={600}>{staff.full_name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {staff.department} • {staff.role}
                                  </Typography>
                                </Box>
                                <Chip 
                                  label={`Joined: ${staff.join_date}`} 
                                  size="small" 
                                  icon={<CalendarTodayIcon />}
                                />
                              </Box>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>Recent Staff Activity (Last 7 Days)</Typography>
                      {recentLogins.length === 0 ? (
                        <Paper sx={{ p: 3, textAlign: 'center' }}>
                          <Typography color="text.secondary">No recent staff activity</Typography>
                        </Paper>
                      ) : (
                        <Stack spacing={1}>
                          {recentLogins.map((staff, idx) => (
                            <Paper key={idx} sx={{ p: 2, borderRadius: 2, '&:hover': { bgcolor: '#f8fafc' } }}>
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                  <Typography variant="subtitle2" fontWeight={600}>{staff.full_name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {staff.department} • {staff.role}
                                  </Typography>
                                </Box>
                                <Chip 
                                  label={`Last active: ${staff.last_active}`} 
                                  size="small" 
                                  color="success"
                                  icon={<HistoryIcon />}
                                />
                              </Box>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Grid>
                  </Grid>

                  {/* Department Statistics */}
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 3, mb: 2 }}>Department Activity Statistics</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell>Department</TableCell>
                          <TableCell align="center">Staff Count</TableCell>
                          <TableCell align="center">Requisitions</TableCell>
                          <TableCell align="center">Items Requested</TableCell>
                          <TableCell align="center">Pending Reqs</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {departmentStats.map((dept, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>{dept.department}</TableCell>
                            <TableCell align="center">
                              <Chip label={dept.staff_count} size="small" color="primary" />
                            </TableCell>
                            <TableCell align="center">{dept.requisition_count || 0}</TableCell>
                            <TableCell align="center">{dept.total_items_requested || 0}</TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={dept.pending_requisitions || 0} 
                                size="small" 
                                color={dept.pending_requisitions > 0 ? 'warning' : 'default'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Performance Tab */}
              {activeTab === 3 && (
                <Box sx={{ p: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>Staff Performance Metrics</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Based on bookings, F&B orders, and payments processed
                  </Typography>
                  
                  {staffPerformance.length === 0 ? (
                    <Paper sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="text.secondary">No performance data available</Typography>
                    </Paper>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell>Staff Name</TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell align="center">Bookings</TableCell>
                            <TableCell align="center">F&B Orders</TableCell>
                            <TableCell align="center">Payments</TableCell>
                            <TableCell align="center">Total Activity</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {staffPerformance.map((staff, idx) => {
                            const totalActivity = (staff.bookings_processed || 0) + (staff.fb_orders_processed || 0) + (staff.payments_processed || 0);
                            return (
                              <TableRow key={idx} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={500}>
                                    {staff.first_name} {staff.other_names}
                                  </Typography>
                                </TableCell>
                                <TableCell>{staff.department}</TableCell>
                                <TableCell>
                                  <Chip label={staff.user_type} size="small" />
                                </TableCell>
                                <TableCell align="center">
                                  <Badge badgeContent={staff.bookings_processed || 0} color="primary" />
                                </TableCell>
                                <TableCell align="center">
                                  <Badge badgeContent={staff.fb_orders_processed || 0} color="secondary" />
                                </TableCell>
                                <TableCell align="center">
                                  <Badge badgeContent={staff.payments_processed || 0} color="success" />
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={totalActivity} 
                                    size="small" 
                                    color={totalActivity > 50 ? 'success' : totalActivity > 20 ? 'primary' : 'default'}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
            </Paper>
          </>
        )}
      </Box>

      {/* Department Staff Dialog */}
      <Dialog 
        open={departmentDialogOpen} 
        onClose={() => setDepartmentDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: alpha(DEPARTMENT_COLORS[selectedDepartment] || '#6366F1', 0.1) }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {selectedDepartment} Department
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Staff: {departmentStaff.length}
              </Typography>
            </Box>
            <IconButton onClick={() => setDepartmentDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {departmentStaff.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">No staff members in this department</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell>Staff ID</TableCell>
                    <TableCell>Full Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {departmentStaff.map((staff) => (
                    <TableRow key={staff.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {staff.staff_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 28, height: 28 }}>
                            {staff.full_name?.charAt(0)}
                          </Avatar>
                          <Typography variant="body2" fontWeight={500}>{staff.full_name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={staff.role} 
                          size="small"
                          sx={{ bgcolor: alpha(ROLE_COLORS[staff.role] || '#6B7280', 0.1), color: ROLE_COLORS[staff.role] || '#6B7280' }}
                        />
                      </TableCell>
                      <TableCell>{staff.email}</TableCell>
                      <TableCell>{staff.phone}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => {
                            setDepartmentDialogOpen(false);
                            handleStaffClick(staff);
                          }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDepartmentDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Staff Detail Dialog */}
      <Dialog open={staffDetailOpen} onClose={() => setStaffDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: alpha('#6366F1', 0.05) }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>Staff Details</Typography>
            <IconButton onClick={() => setStaffDetailOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedStaff && (
            <Stack spacing={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: alpha(ROLE_COLORS[selectedStaff.role] || '#6B7280', 0.2) }}>
                  <PeopleIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{selectedStaff.full_name}</Typography>
                  <Typography variant="body2" color="text.secondary">Staff ID: {selectedStaff.staff_id}</Typography>
                </Box>
              </Box>
              
              <Divider />
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Department</Typography>
                  <Typography variant="body2" fontWeight={500}>{selectedStaff.department}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Role</Typography>
                  <Typography variant="body2" fontWeight={500}>{selectedStaff.role}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body2">{selectedStaff.email}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body2">{selectedStaff.phone}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Gender</Typography>
                  <Typography variant="body2">{selectedStaff.gender || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Hire Date</Typography>
                  <Typography variant="body2">{selectedStaff.hire_date}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                  <Typography variant="body2">{selectedStaff.last_updated}</Typography>
                </Grid>
              </Grid>
              
              <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
                <Typography variant="caption">
                  To delete this staff member, go to the Staff Directory tab and click the delete icon.
                </Typography>
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStaffDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ bgcolor: alpha('#ef4444', 0.1) }}>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="error" />
            <Typography variant="h6" fontWeight={600}>Confirm Deletion</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{staffToDelete?.full_name}</strong>?
            <br />
            <br />
            <strong>Staff ID:</strong> {staffToDelete?.staff_id}<br />
            <strong>Department:</strong> {staffToDelete?.department}<br />
            <strong>Role:</strong> {staffToDelete?.role}<br />
            <br />
            This action cannot be undone. The staff member will be permanently removed from the system.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained" 
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Staff'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Gm_StaffDashboard;